import React, { useState, useEffect, useCallback } from 'react';
import './DirectorAppointments.css';
import WeekCalendar from './WeekCalendar';
import { startOfWeek, addDays, ymd, formatWeekRange } from '../utils/appointmentWeek';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const SENIOR_FALLBACK = ['rcarballo', 'acorvino'];

// Shared week navigation bar (reuses WeekCalendar's nav styling).
const WeekNav = ({ weekStart, onPrev, onNext, onToday }) => (
  <div className="wc-nav">
    <button className="wc-nav-btn" onClick={onPrev}>◀ Previous Week</button>
    <div className="wc-nav-center">
      <div className="wc-week-range">Week of {formatWeekRange(weekStart)}</div>
      <button className="wc-today-link" onClick={onToday}>This Week</button>
    </div>
    <button className="wc-nav-btn" onClick={onNext}>Next Week ▶</button>
  </div>
);

const DirectorAppointments = ({ onBack, user }) => {
  const isSenior = user.is_senior_director != null
    ? user.is_senior_director
    : SENIOR_FALLBACK.includes((user.username || '').toLowerCase());

  // Senior loads a directors[] tree; a regular director loads managers[].
  const [directors, setDirectors] = useState([]);
  const [managers, setManagers] = useState([]);
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Drill-down selection (by id; nodes are re-derived from fresh data each week).
  const [selectedDirectorId, setSelectedDirectorId] = useState(null);
  const [selectedManagerId, setSelectedManagerId] = useState(null);

  const headers = { 'X-User-ID': String(user.userId) };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const start = ymd(weekStart);
    const end = ymd(addDays(weekStart, 6));
    const url = isSenior
      ? `${API_URL}/api/senior-director/overview/?start=${start}&end=${end}`
      : `${API_URL}/api/super-admin/manager-appointments/?start=${start}&end=${end}`;
    try {
      const res = await fetch(url, { headers });
      if (!res.ok) throw new Error('Failed to load data');
      const data = await res.json();
      if (isSenior) setDirectors(data);
      else setManagers(data);
    } catch {
      setError('Failed to load appointments.');
    } finally {
      setLoading(false);
    }
  }, [weekStart, isSenior, user.userId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const goPrev = () => setWeekStart((w) => addDays(w, -7));
  const goNext = () => setWeekStart((w) => addDays(w, 7));
  const goToday = () => setWeekStart(startOfWeek(new Date()));

  // Re-derive selected nodes from the current week's data.
  const selectedDirector = isSenior
    ? directors.find((d) => d.director_id === selectedDirectorId) || null
    : null;
  const managerPool = isSenior ? (selectedDirector ? selectedDirector.managers : []) : managers;
  const selectedManager = managerPool.find((m) => m.manager_id === selectedManagerId) || null;

  const nameOf = (n) => n.full_name || n.username;

  // ── Breadcrumb ──────────────────────────────────────────────────
  const renderBreadcrumb = () => {
    const crumbs = [];
    if (isSenior) {
      crumbs.push({ label: 'Directors', onClick: () => { setSelectedDirectorId(null); setSelectedManagerId(null); } });
      if (selectedDirector) {
        crumbs.push({ label: nameOf(selectedDirector), onClick: () => setSelectedManagerId(null) });
      }
    } else {
      crumbs.push({ label: 'My Team', onClick: () => setSelectedManagerId(null) });
    }
    if (selectedManager) {
      crumbs.push({ label: nameOf(selectedManager), onClick: null });
    }
    return (
      <div className="da-breadcrumb">
        {crumbs.map((c, i) => (
          <span key={i} className="da-crumb">
            {i > 0 && <span className="da-crumb-sep">›</span>}
            {c.onClick
              ? <button className="da-crumb-link" onClick={c.onClick}>{c.label}</button>
              : <span className="da-crumb-current">{c.label}</span>}
          </span>
        ))}
      </div>
    );
  };

  // ── List of directors (senior, top level) ───────────────────────
  const renderDirectorList = () => {
    if (directors.length === 0) {
      return <div className="da-empty">No directors found.</div>;
    }
    return (
      <div className="da-cards">
        {directors.map((d) => (
          <button
            key={d.director_id}
            className="da-node-card"
            onClick={() => { setSelectedDirectorId(d.director_id); setSelectedManagerId(null); }}
          >
            <div className="da-node-info">
              <span className="da-node-name">{nameOf(d)}</span>
              <span className="da-node-sub">
                {d.manager_count} manager{d.manager_count !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="da-node-right">
              <span className={`da-count-badge${d.appointment_count === 0 ? ' zero' : ''}`}>
                {d.appointment_count} appt{d.appointment_count !== 1 ? 's' : ''}
              </span>
              <span className="da-chevron">›</span>
            </div>
          </button>
        ))}
      </div>
    );
  };

  // ── List of managers (a director's team) ────────────────────────
  const renderManagerList = () => {
    if (managerPool.length === 0) {
      return <div className="da-empty">No managers in this team.</div>;
    }
    return (
      <div className="da-cards">
        {managerPool.map((m) => (
          <button
            key={m.manager_id}
            className="da-node-card"
            onClick={() => setSelectedManagerId(m.manager_id)}
          >
            <div className="da-node-info">
              <span className="da-node-name">{nameOf(m)}</span>
              <span className="da-node-sub">@{m.username}</span>
            </div>
            <div className="da-node-right">
              <span className={`da-count-badge${m.appointment_count === 0 ? ' zero' : ''}`}>
                {m.appointment_count} appt{m.appointment_count !== 1 ? 's' : ''}
              </span>
              <span className="da-chevron">›</span>
            </div>
          </button>
        ))}
      </div>
    );
  };

  // ── Body by drill-down level ────────────────────────────────────
  const renderBody = () => {
    if (loading) return <div className="da-loading">Loading...</div>;
    if (error) return <div className="da-error">{error}</div>;

    if (selectedManager) {
      return (
        <WeekCalendar
          weekStart={weekStart}
          appointments={selectedManager.appointments}
          showNav={false}
        />
      );
    }
    if (isSenior && !selectedDirector) return renderDirectorList();
    return renderManagerList();
  };

  return (
    <div className="da-container">
      <div className="da-header">
        <button className="da-back-btn" onClick={onBack}>Back</button>
        <h2>{isSenior ? 'All Field Appointments' : 'Team Field Appointments'}</h2>
      </div>

      {renderBreadcrumb()}

      <WeekNav weekStart={weekStart} onPrev={goPrev} onNext={goNext} onToday={goToday} />

      {renderBody()}
    </div>
  );
};

export default DirectorAppointments;
